<template>
    <div class="px-3 py-3 sm:px-5 sm:py-5">
        <!-- Header -->
        <div class="flex flex-col center-content mb-5 sm:mb-7">
            <h2 class="font-bold text-center mb-2 text-xl sm:text-2xl bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                Impact Score Leaderboard
            </h2>
            <p class="text-center text-gray-600 mb-3 text-sm sm:text-base">
                Select a foundation to view its campaign leaderboard
            </p>

            <!-- Toggle for N/A Foundations -->
            <div class="text-center">
                <a v-if="showAll"
                   href="/admin/reports/impact-leaderboard"
                   class="inline-flex items-center text-sm text-pink-600 hover:text-pink-700 font-medium">
                    <svg class="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    Hide foundations without impact scores
                </a>
                <a v-else
                   href="/admin/reports/impact-leaderboard?show_all=1"
                   class="inline-flex items-center text-sm text-gray-600 hover:text-gray-700 font-medium">
                    <svg class="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Show all foundations (including N/A scores)
                </a>
            </div>
        </div>

        <template v-if="totalWithScores > 0">
            <!-- Simple Filter Controls -->
            <div class="bg-white rounded-lg shadow-sm border border-pink-100 p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Search Box -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Search Foundations</label>
                        <input type="text"
                               v-model="searchQuery"
                               placeholder="Search by name..."
                               class="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 text-sm">
                    </div>

                    <!-- Min Donors Filter -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            Minimum Donors: <span class="font-bold text-pink-600">{{ localMinDonors }}</span>
                        </label>
                        <input type="number"
                               v-model="localMinDonors"
                               min="0"
                               class="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 text-sm">
                    </div>
                </div>

                <!-- Filter Summary -->
                <div class="mt-4 flex justify-between items-center">
                    <button v-if="searchQuery || localMinDonors !== minDonors"
                            @click="resetFilters()"
                            class="text-sm text-pink-600 hover:text-pink-700 font-medium">
                        <i class="fas fa-undo mr-1"></i> Reset Filters
                    </button>
                    <div class="text-sm text-gray-600 font-medium ml-auto">
                        Showing <span class="font-bold text-pink-600">{{ filteredFoundations.length }}</span> foundation<span v-if="filteredFoundations.length !== 1">s</span>
                    </div>
                </div>
            </div>

            <!-- Performance Summary -->
            <div class="mb-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg shadow-md overflow-hidden">
                <div class="px-6 py-4 bg-white bg-opacity-60 backdrop-blur-sm">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Foundation Performance Summary</h3>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                        <!-- Total Foundations -->
                        <div class="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <svg class="h-6 w-6 sm:h-8 sm:w-8 text-pink-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <div class="ml-3 sm:ml-4">
                                    <p class="text-xs sm:text-sm font-medium text-gray-500">Total Foundations</p>
                                    <p class="text-xl sm:text-2xl font-bold text-gray-900">{{ formatNumber(totalWithScores) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Average Score -->
                        <div class="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <svg class="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div class="ml-3 sm:ml-4">
                                    <p class="text-xs sm:text-sm font-medium text-gray-500">Average Score</p>
                                    <p class="text-xl sm:text-2xl font-bold text-gray-900">{{ averageScore.toFixed(1) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- About Impact Scores -->
                    <div class="bg-white rounded-lg p-4 shadow-sm mt-4">
                        <h4 class="text-sm font-semibold text-gray-700 mb-3">About Impact Scores</h4>
                        <div class="text-sm text-gray-600">
                            <p class="mb-2">Impact scores measure campaign performance using:</p>
                            <ul class="list-none space-y-1">
                                <li class="flex items-center">
                                    <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">40%</span>
                                    <span><strong>Donor Count</strong> - Unique donors contributing</span>
                                </li>
                                <li class="flex items-center">
                                    <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">25%</span>
                                    <span><strong>Avg $/Donor</strong> - Average donation amount</span>
                                </li>
                                <li class="flex items-center">
                                    <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">15%</span>
                                    <span><strong>Reach</strong> - Unique campaign page views</span>
                                </li>
                                <li class="flex items-center">
                                    <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">10%</span>
                                    <span><strong>Fundraising</strong> - Total funds raised</span>
                                </li>
                                <li class="flex items-center">
                                    <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">10%</span>
                                    <span><strong>Conversion</strong> - Donations per view</span>
                                </li>
                            </ul>
                            <p class="mt-3 text-xs text-gray-500">
                                Scores use tier-based scoring for absolute metrics and relative scoring within foundations.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Empty State -->
        <div v-if="foundations.length === 0" class="text-center py-12">
            <div class="text-6xl mb-4">🏆</div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">No Foundations Found</h3>
            <p class="text-gray-600 mb-4">
                No foundations with impact scores found. Run the impact score calculation command first:
            </p>
            <code class="bg-yellow-100 px-4 py-2 rounded text-sm">php artisan campaign:calculate-impact-scores</code>
        </div>

        <template v-else>
            <!-- Empty State for Filtered Results -->
            <div v-if="filteredFoundations.length === 0" class="text-center py-12">
                <div class="text-6xl mb-4">🔍</div>
                <h3 class="text-xl font-semibold text-gray-900 mb-2">No Foundations Match Your Filters</h3>
                <p class="text-gray-600 mb-4">Try adjusting your filters or search criteria</p>
                <button @click="resetFilters()"
                        class="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 font-medium transition-all">
                    Clear All Filters
                </button>
            </div>

            <!-- Mobile Card Layout (< 640px) -->
            <div v-if="filteredFoundations.length > 0" class="sm:hidden space-y-4">
                <div v-for="foundation in filteredFoundations" :key="foundation.id"
                     class="bg-white rounded-lg shadow-md border border-pink-100 overflow-hidden">
                    <div class="p-4">
                        <h3 class="text-lg font-bold mb-3 text-gray-900">{{ foundation.name }}</h3>

                        <!-- Score -->
                        <div class="flex items-center justify-between mb-4">
                            <div>
                                <div class="text-3xl font-bold text-pink-600">{{ foundation.impact_score ? foundation.impact_score.toFixed(1) : 'N/A' }}</div>
                                <div class="text-sm text-gray-600">Impact Score</div>
                            </div>
                        </div>

                        <!-- Stats Grid -->
                        <div class="grid grid-cols-3 gap-4 mb-4 text-center">
                            <div>
                                <div class="text-xs text-gray-600">Campaigns</div>
                                <div class="text-lg font-semibold">{{ formatNumber(foundation.campaigns_count) }}</div>
                            </div>
                            <div>
                                <div class="text-xs text-gray-600">Donors</div>
                                <div class="text-lg font-semibold text-pink-600">{{ formatNumber(foundation.donor_count) }}</div>
                            </div>
                            <div>
                                <div class="text-xs text-gray-600">Raised</div>
                                <div class="text-lg font-semibold text-green-600">${{ formatNumber(foundation.total_raised) }}</div>
                            </div>
                        </div>

                        <!-- Action Button -->
                        <a :href="`/admin/reports/impact-leaderboard/${foundation.id}`"
                           class="block w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white text-center py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-pink-700 transition-all">
                            View Campaign Leaderboard
                        </a>
                    </div>
                </div>
            </div>

            <!-- Desktop Table (≥ 640px) -->
            <div v-if="filteredFoundations.length > 0"
                 class="hidden sm:block bg-white rounded-lg shadow-sm border border-pink-100 overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gradient-to-r from-pink-500 to-pink-600">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Foundation Name</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Impact Score</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Campaigns</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Donors</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total Raised</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="foundation in filteredFoundations" :key="foundation.id"
                            class="hover:bg-pink-50 transition-colors">
                            <!-- Foundation Name -->
                            <td class="px-6 py-4">
                                <div class="text-sm font-medium text-gray-900">{{ foundation.name }}</div>
                            </td>

                            <!-- Impact Score -->
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span v-if="foundation.impact_score" class="text-sm font-semibold text-pink-600">
                                    {{ foundation.impact_score.toFixed(1) }}
                                </span>
                                <span v-else class="text-sm text-gray-400">N/A</span>
                            </td>

                            <!-- Campaigns -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatNumber(foundation.campaigns_count) }}</td>

                            <!-- Donors -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{{ formatNumber(foundation.donor_count) }}</td>

                            <!-- Total Raised -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">${{ formatNumber(foundation.total_raised) }}</td>

                            <!-- Action -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm">
                                <a :href="`/admin/reports/impact-leaderboard/${foundation.id}`"
                                   class="text-pink-600 hover:text-pink-900 font-medium">
                                    View Leaderboard →
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'ImpactLeaderboard',

    props: {
        foundations: {
            type: Array,
            required: true
        },
        showAll: {
            type: Boolean,
            default: false
        },
        minDonors: {
            type: Number,
            default: 10
        },
        maxDonors: {
            type: Number,
            default: null
        },
        averageScore: {
            type: Number,
            required: true
        },
        totalWithScores: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            searchQuery: '',
            localMinDonors: this.minDonors
        }
    },

    computed: {
        filteredFoundations() {
            let filtered = this.foundations

            // Search filter
            if (this.searchQuery) {
                filtered = filtered.filter(f =>
                    f.name.toLowerCase().includes(this.searchQuery.toLowerCase())
                )
            }

            // Donor count filter
            filtered = filtered.filter(f =>
                (f.donor_count || 0) >= this.localMinDonors
            )

            // Sort by most recent first (created_at descending)
            return filtered.sort((a, b) => {
                const dateA = new Date(a.created_at || 0)
                const dateB = new Date(b.created_at || 0)
                return dateB - dateA  // Most recent first
            })
        }
    },

    methods: {
        formatNumber(num) {
            return (num || 0).toLocaleString()
        },

        resetFilters() {
            this.searchQuery = ''
            this.localMinDonors = this.minDonors
        }
    }
}
</script>
